import {
    FocusEvent,
    RefObject,
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { ListOnItemsRenderedProps, VariableSizeList } from 'react-window';

import {
    ListRow,
    RowIsFocusable,
    focusableIndexFrom,
    indexOfKeyNear,
} from './list-key-action';
import { focusListRow, listRowIndex, openOverlaysOf } from './list-row-focus';
import { createRowStore } from './row-index-store';
import useListKeys from './useListKeys';

interface Options<Row extends ListRow> {
    containerRef: RefObject<HTMLElement>;
    /** The react-window list, if the list is one. */
    listRef?: RefObject<VariableSizeList>;
    rows: Row[];
    /**
     * Each row's key, from uniqueRowKeys. Focus and the Tab stop follow a row
     * by it through new rows. Give react-window the same keys (listItemKey).
     */
    rowKeys: string[];
    rowIsFocusable: RowIsFocusable<Row>;
    /** The row that Tab enters the list at until a row has had focus. */
    entryIndex: () => number;
    /**
     * Called with the row that focus entered, however it got there: a key, a
     * click on one of the row's buttons, Tab, or the list putting focus back.
     * The browser shows a focus ring only when `focusIsVisible`: not after a
     * click.
     */
    onRowFocus?: (index: number, focusIsVisible: boolean) => void;
    /** Called when focus moves from the list to an element outside it. */
    onFocusLeave?: () => void;
}

interface FocusedRow {
    key: string;
    index: number;
    /** The row keys that `index` counts in. */
    rowKeys: string[];
}

// The row at the index of a row that left, else the row above.
const nearestFocusableIndex = <Row extends ListRow>(
    rows: Row[],
    rowIsFocusable: RowIsFocusable<Row>,
    index: number,
) => {
    const below = focusableIndexFrom(rows, rowIsFocusable, index, 1);
    return below === -1
        ? focusableIndexFrom(
              rows,
              rowIsFocusable,
              Math.min(index, rows.length) - 1,
              -1,
          )
        : below;
};

// The row nearest to a row that left, among the rows still in the list: the
// first one below it in its own rows, else the first one above it. Several
// rows can leave at once, so the index of the row that left cannot find its
// neighbour in the new rows.
const nearestRemainingIndex = <Row extends ListRow>(
    rows: Row[],
    rowKeys: string[],
    rowIsFocusable: RowIsFocusable<Row>,
    { index, rowKeys: previousKeys }: FocusedRow,
) => {
    const indexByKey = new Map<string, number>();
    rows.forEach((row, at) => {
        if (rowIsFocusable(row)) {
            indexByKey.set(rowKeys[at], at);
        }
    });
    const remainingIndex = (previousIndex: number) =>
        indexByKey.get(previousKeys[previousIndex]) ?? -1;

    for (let below = index + 1; below < previousKeys.length; below += 1) {
        const at = remainingIndex(below);
        if (at !== -1) {
            return at;
        }
    }
    for (let above = index - 1; above >= 0; above -= 1) {
        const at = remainingIndex(above);
        if (at !== -1) {
            return at;
        }
    }
    return nearestFocusableIndex(rows, rowIsFocusable, index);
};

/**
 * Makes a list one Tab stop: the row that last had focus holds it, and the
 * other rows and their buttons are out of the Tab order. It also gives the
 * list's container its key handler.
 *
 * The container is not a control and never gets focus. It only takes the
 * focus and key events of its rows. A role and a tab stop on it would put an
 * empty Tab stop in front of the list. Sonar reads its handlers as a sign of
 * a control, so each container carries a NOSONAR that points here.
 */
const useListTabStop = <Row extends ListRow>({
    containerRef,
    listRef,
    rows,
    rowKeys,
    rowIsFocusable,
    entryIndex,
    onRowFocus,
    onFocusLeave,
}: Options<Row>) => {
    // This is one store for the life of the list. A new Tab stop changes the
    // row that the store holds, not the store object. So the hooks below that
    // list the store do not run again because of it. The store names the
    // row by its key, not its index, so a row that moves keeps the stop
    // without a render of the rows around it.
    const [tabStopStore] = useState(() => createRowStore<string>());
    const renderedRowsRef = useRef<ListOnItemsRenderedProps | null>(null);
    const focusedRowRef = useRef<FocusedRow | null>(null);
    const rowUnmountedWithFocusRef = useRef(false);

    // react-window renders only the rows near the view, and a row that is not
    // rendered cannot take focus. A row in view holds the Tab stop instead, so
    // Tab can still enter the list.
    const placeTabStop = useCallback(
        (index: number) => {
            const renderedRows = renderedRowsRef.current;
            const indexIsRendered =
                renderedRows === null ||
                (index >= renderedRows.overscanStartIndex &&
                    index <= renderedRows.overscanStopIndex);
            if (indexIsRendered) {
                tabStopStore.set(rowKeys[index] ?? null);
                return;
            }

            const firstInView = renderedRows.visibleStartIndex;
            const inViewOrBelow = focusableIndexFrom(
                rows,
                rowIsFocusable,
                firstInView,
                1,
            );
            const standInIndex =
                inViewOrBelow === -1
                    ? focusableIndexFrom(
                          rows,
                          rowIsFocusable,
                          firstInView - 1,
                          -1,
                      )
                    : inViewOrBelow;
            tabStopStore.set(rowKeys[standInIndex] ?? null);
        },
        [tabStopStore, rowKeys, rows, rowIsFocusable],
    );

    // The row that last had focus keeps the Tab stop through a search or new
    // data while it is in the list. Until then, and once it is gone, the
    // entry row holds it. The entry row also holds it while the row cannot
    // take focus, as a PBX section title cannot during a search.
    const tabStopIndex = useCallback(() => {
        const focusedRow = focusedRowRef.current;
        const index =
            focusedRow === null ? -1 : indexOfKeyNear(rowKeys, focusedRow);
        if (index === -1) {
            focusedRowRef.current = null;
            return entryIndex();
        }
        focusedRow.index = index;
        focusedRow.rowKeys = rowKeys;
        return rowIsFocusable(rows[index]) ? index : entryIndex();
    }, [rows, rowKeys, rowIsFocusable, entryIndex]);

    const followFocusedRow = useCallback(
        (index: number) => {
            focusedRowRef.current = { key: rowKeys[index], index, rowKeys };
            placeTabStop(index);
        },
        [rowKeys, placeTabStop],
    );

    // Focus goes to the page when its row element unmounts. If the row moved,
    // focus goes back to it. If the row left the list, focus goes to the
    // nearest row. If the user scrolled the row away, focus stays on the page.
    const refocusUnmountedRow = useCallback(() => {
        if (!rowUnmountedWithFocusRef.current) {
            return;
        }
        rowUnmountedWithFocusRef.current = false;
        const focusedRow = focusedRowRef.current;
        if (focusedRow === null || document.activeElement !== document.body) {
            return;
        }

        const index = indexOfKeyNear(rowKeys, focusedRow);
        if (index === focusedRow.index) {
            // Scrolled away: focus stays on the page, so it left the list.
            onFocusLeave?.();
            return;
        }
        const indexToFocus =
            index === -1
                ? nearestRemainingIndex(
                      rows,
                      rowKeys,
                      rowIsFocusable,
                      focusedRow,
                  )
                : index;
        if (indexToFocus === -1) {
            return;
        }
        // React cannot flush the scroll while it commits. So focus waits until
        // the commit ends.
        queueMicrotask(() => {
            if (document.activeElement === document.body) {
                focusListRow(containerRef.current, listRef, indexToFocus);
            }
        });
    }, [containerRef, listRef, rows, rowKeys, rowIsFocusable, onFocusLeave]);

    // While focus is inside a row, that row holds the Tab stop. React can give
    // the focused row element a new index, and no focus event occurs then.
    useLayoutEffect(() => {
        refocusUnmountedRow();
        const focusedIndex = listRowIndex(
            containerRef.current,
            document.activeElement,
        );
        if (focusedIndex !== null) {
            followFocusedRow(focusedIndex);
        } else if (!containerRef.current?.contains(document.activeElement)) {
            placeTabStop(tabStopIndex());
        }
    }, [
        containerRef,
        refocusUnmountedRow,
        followFocusedRow,
        placeTabStop,
        tabStopIndex,
    ]);

    const onItemsRendered = useCallback(
        (renderedRows: ListOnItemsRenderedProps) => {
            renderedRowsRef.current = renderedRows;
            refocusUnmountedRow();
            placeTabStop(tabStopIndex());
        },
        [refocusUnmountedRow, placeTabStop, tabStopIndex],
    );

    const onFocus = useCallback(
        (event: FocusEvent<HTMLElement>) => {
            const index = listRowIndex(
                containerRef.current,
                event.target as Element,
            );
            if (index !== null) {
                followFocusedRow(index);
                onRowFocus?.(
                    index,
                    (event.target as Element).matches(':focus-visible'),
                );
            }
        },
        [containerRef, followFocusedRow, onRowFocus],
    );

    // Focus that goes to no element leaves the list only while the window
    // keeps focus, as after a click on a part of the page that takes no focus.
    // When the window loses focus, it comes back to the row. A menu, listbox,
    // combobox or popover that a row opens renders outside the container;
    // focus in it stays with the row, whose trigger controls it.
    const onBlur = useCallback(
        (event: FocusEvent<HTMLElement>) => {
            const next = event.relatedTarget as Node | null;
            const container = containerRef.current;
            const focusIsInRowOverlay =
                next !== null &&
                container !== null &&
                openOverlaysOf(container).some((panel) => panel.contains(next));
            const focusLeftList =
                next === null
                    ? document.hasFocus()
                    : !container?.contains(next) && !focusIsInRowOverlay;
            if (focusLeftList) {
                onFocusLeave?.();
            }
        },
        [containerRef, onFocusLeave],
    );

    const onRowUnmountWithFocus = useCallback(() => {
        rowUnmountedWithFocusRef.current = true;
    }, []);

    const onKeyDown = useListKeys({
        containerRef,
        listRef,
        rows,
        rowIsFocusable,
    });

    return {
        tabStopStore,
        onFocus,
        onBlur,
        onKeyDown,
        onItemsRendered,
        onRowUnmountWithFocus,
    };
};

export default useListTabStop;
